import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { MessageSquare, Sparkles, SquarePen, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet'

interface MobileAgentDrawerProps {
  launcher?: ReactNode
  onComposerFocus?: () => void
  open: boolean
  onOpenChange: (open: boolean) => void
  showActions?: boolean
  onThreads: () => void
  onNewChat: () => void
  onKeyboardChange?: (open: boolean) => void
  children: ReactNode
}

/** Modal semantics, focus return and Escape come from the shared Sheet primitive. */
export function MobileAgentDrawer({ launcher, onComposerFocus, open, onOpenChange, showActions = true, onThreads, onNewChat, onKeyboardChange, children }: MobileAgentDrawerProps) {
  const launcherRef = useRef<HTMLDivElement>(null)
  const [keyboardInset, setKeyboardInset] = useState(0)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const [viewportStyle, setViewportStyle] = useState<CSSProperties>()
  useEffect(() => {
    const viewport = window.visualViewport
    let frame = 0
    const update = () => {
      if (!viewport) return
      setKeyboardInset(Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop))
      const keyboardOpen = viewport.height < window.innerHeight * 0.8
      onKeyboardChange?.(keyboardOpen)
      const height = viewport.height * 0.9
      setViewportStyle({ top: viewport.offsetTop + viewport.height - height, height, bottom: 'auto' })
    }
    update()
    const scheduleUpdate = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(update)
    }
    viewport?.addEventListener('resize', scheduleUpdate)
    viewport?.addEventListener('scroll', scheduleUpdate)
    return () => {
      cancelAnimationFrame(frame)
      viewport?.removeEventListener('resize', scheduleUpdate)
      viewport?.removeEventListener('scroll', scheduleUpdate)
    }
  }, [onKeyboardChange])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {launcher ? (
        <div ref={launcherRef} tabIndex={-1} hidden={open}
          className="pointer-events-none fixed inset-x-0 bottom-0 z-20 p-3 pb-[max(1.5rem,calc(env(safe-area-inset-bottom)*2))] outline-none"
          style={{ bottom: keyboardInset }}>
          {!open && <div className="pointer-events-auto">{launcher}</div>}
        </div>
      ) : (
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex justify-end p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <SheetTrigger asChild>
          <Button size="form" className="pointer-events-auto rounded-full shadow-lg">
            <Sparkles aria-hidden /> Ask AI
          </Button>
        </SheetTrigger>
      </div>
      )}
      <SheetContent
        side="bottom"
        hideCloseButton
        aria-describedby={undefined}
        className="h-[90dvh] min-h-0 gap-0 overflow-hidden rounded-t-xl pb-[env(safe-area-inset-bottom)] data-[state=open]:slide-in-from-bottom-8 data-[state=closed]:slide-out-to-bottom-4 data-[state=open]:duration-[240ms] data-[state=closed]:duration-[160ms] ease-[cubic-bezier(0.16,1,0.3,1)] transition-none motion-reduce:animate-none"
        overlayClassName="motion-reduce:animate-none"
        style={viewportStyle}
        onCloseAutoFocus={launcher ? (event) => {
          event.preventDefault()
          launcherRef.current?.focus()
        } : undefined}
        onOpenAutoFocus={(event) => {
          // A mini-composer interaction continues typing in the full composer.
          event.preventDefault()
          if (launcher && onComposerFocus) onComposerFocus()
          else titleRef.current?.focus()
        }}
      >
        <div className="flex shrink-0 items-center justify-between border-b px-4">
          <SheetTitle ref={titleRef} tabIndex={-1}>Ask AI</SheetTitle>
          <SheetClose asChild>
            <Button variant="ghost" size="form"><X aria-hidden /> Close</Button>
          </SheetClose>
        </div>
        {showActions && <div className="flex shrink-0 items-center justify-between border-b px-4">
          <Button variant="ghostMuted" size="form" onClick={onThreads}><MessageSquare aria-hidden /> Threads</Button>
          <Button variant="ghostMuted" size="form" onClick={onNewChat}><SquarePen aria-hidden /> New chat</Button>
        </div>}
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      </SheetContent>
    </Sheet>
  )
}
